<template>
	<div>
		<ja-card
			v-for="(item, index) in data"
			:key="index"
			:disabled="!item.isSelected"
			class="card-toggle border-radius--0 mb--0"
			:class="{ 'card-toggle--expand': item.isContentOpened }"
		>
			<div
				class="card-toggle__header"
				:class="{ 'no-content': !hasContent(item) }"
			>
				<div class="selectable-circle" @click="changeItemSelection(item)">
					<ja-multiselect-circle
						v-if="options.deselectableItems"
						:selected="item.isSelected"
						name="select-item"
					/>
				</div>
				<div class="info">
					<div class="info-title">{{ item.title }}</div>
					<div class="info-subtitle">{{ item.subtitle }}</div>
					<div class="info-description">
						<slot name="infoDescription" :item="item">
							{{ item.description }}
						</slot>
					</div>
				</div>
				<ja-button
					v-if="options.removableItems"
					name="remove-sim"
					class="transparent danger body-text-1"
					@click.native="emitRemoveEvent(item)"
				>
					{{ $t('remove') }}
				</ja-button>

				<div
					v-if="hasContent(item)"
					class="card-toggle__button"
					@click="toggleContent(item)"
				>
					<svgicon name="arrow-right"></svgicon>
				</div>
			</div>
			<div class="card-toggle__content">
				<slot name="content" :item="item"></slot>
			</div>
		</ja-card>
	</div>
</template>

<script>
import MultiselectCircle from './MultiselectCircle';
import { Card, Button } from '@ui';

export default {
	components: {
		jaCard: Card,
		jaButton: Button,
		jaMultiselectCircle: MultiselectCircle
	},
	props: {
		items: {
			type: Array,
			default: () => []
		},
		itemOptions: {
			type: Object,
			default: () => {}
		},
		autoClose: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			data: [],
			options: {
				selectionId: 'id',
				titleProperties: [],
				subtitleProperties: [],
				descriptionProperties: [],
				removableItems: false,
				deselectableItems: true,
				checkForContentBy: ''
			}
		};
	},
	watch: {
		items() {
			this.dataAdapter();
			if (this.clearOnUpdate) {
				this.selectedData = [];
			}
		}
	},
	mounted() {
		if (this.items) {
			this.dataAdapter();
		}
	},
	methods: {
		hasContent(item) {
			// TODO: fix this to work with boolean values
			if (this.options.checkForContentBy) {
				return (
					item[this.options.checkForContentBy] &&
					item[this.options.checkForContentBy].length
				);
			}

			// if option is not provided, assume content is not always avaiable
			return false;
		},
		toggleContent(item) {
			if (this.autoClose) {
				this.data.forEach(i => {
					if (item.id !== i.id) {
						i.isContentOpened = false;
					}
				});
			}

			item.isContentOpened = !item.isContentOpened;
		},
		dataAdapter() {
			// Make sure options are up-to-date before map
			this.updateOptions();

			this.data = this.items.map(item => {
				return {
					...item,
					isSelected: true,
					title: this.setItemProperties(item, 'title'),
					subtitle: this.setItemProperties(item, 'subtitle'),
					description: this.setItemProperties(item, 'description'),
					isContentOpened: false
				};
			});
			this.selectedData = this.data;
		},

		setItemProperties(item, itemProperty) {
			const propertyValues = this.options[`${itemProperty}Properties`];
			const itemValues = [];

			if (!propertyValues) {
				return item[itemProperty];
			}

			propertyValues.forEach(property => itemValues.push(item[property]));

			return itemValues.join(' ');
		},
		updateOptions() {
			this.options = { ...this.options, ...this.itemOptions };
		},
		emitRemoveEvent(item) {
			this.$emit('removeItem', item);
		},
		emitToggleSelectionEvent(item) {
			this.$emit('toggleSelect', item);
		},
		changeItemSelection(item) {
			item.isSelected = !item.isSelected;
			item.isContentOpened = false;
			this.emitToggleSelectionEvent(item);
		}
	}
};
</script>

<style lang="scss" scoped>
.card-toggle {
	padding: 24px;
	display: flex;
	min-height: 64px;
	border: 0;
	border-bottom: 1px solid $table-border;
	flex-wrap: wrap;

	&:disabled,
	&:disabled:hover,
	&.selected:disabled,
	&.selected:disabled:hover {
		cursor: initial !important;
		background-color: $container-bgcolor !important;
		color: $icon-disabled;
		opacity: 1;
		font-weight: 500;

		.info,
		.info-description {
			color: $icon-disabled;
		}

		.card-toggle__button {
			cursor: not-allowed;
			pointer-events: none;
			svg {
				fill: $icon-disabled;
			}
		}
	}

	.selectable-circle {
		align-self: center;
	}

	&__header {
		flex-basis: 100%;
		display: flex;
		justify-content: space-between;
		align-items: flex-start;

		&.no-content {
			button {
				// add 16px (the size of the arrow) to the left space (25%)
				// the main content is 75%
				flex-basis: calc(25% + 16px);
			}
		}
	}

	.info {
		display: block;
		color: $font-header;
		letter-spacing: 0.5px;
		flex-basis: 95%;
		&-title {
			font-family: RobotoMedium;
			font-size: 16px;
			font-weight: 500;
			line-height: 20px;

			@media (min-width: $breakpoint-mid) {
				font-size: 18px;
			}
		}

		&-subtitle {
			font-family: RobotoRegular;
			font-size: 12px;
			line-height: 24px;
			@media (min-width: $breakpoint-mid) {
				font-size: 16px;
			}
		}
		&-description {
			font-family: RobotoRegular;
			font-size: 16px;
			color: $font-dark;
		}

		&-title,
		&-subtitle,
		&-description {
			margin: 2px 0;
		}
	}

	&__button {
		cursor: pointer;
		align-self: flex-end;
		svg {
			fill: $font-blue;
			transform: rotate(90deg);
		}
	}

	&--expand {
		.card-toggle__button {
			svg {
				transform: rotate(-90deg);
			}
		}

		.card-toggle__content {
			display: block;
		}
	}

	&__content {
		display: none;

		.svg-icon {
			cursor: pointer;
		}
	}
}
</style>
